import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiOutlinePlus } from "react-icons/hi";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import {
  useAddAdminUserMutation,
  useDeactivateAdminUserMutation,
  useGetAllAdminUsersMutation,
  useUpdateAdminUserMutation,
} from "../../../features/admin/adminUsers/adminUsersApi";
import { useGetAllAdminRolesMutation } from "../../../features/admin/adminRoles/adminRolesApi";

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("add");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role_id: 1,
  });
  const [selectedId, setSelectedId] = useState(null);

  const [getAllAdminUsers, { data: usersData }] = useGetAllAdminUsersMutation();
  const [getAllAdminRoles, { data: rolesData }] = useGetAllAdminRolesMutation();
  const [addAdminUser, { data: addData, error: addError }] = useAddAdminUserMutation();
  const [updateAdminUser, { data: updateData, error: updateError }] =
    useUpdateAdminUserMutation();
  const [deactivateAdminUser, { data: deleteData, error: deleteError }] =
    useDeactivateAdminUserMutation();

  const token = sessionStorage.getItem("access_token");

  const fetchData = () => {
    getAllAdminUsers({ token });
    getAllAdminRoles({ token });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (usersData?.body) setUsers(usersData.body);
  }, [usersData]);

  useEffect(() => {
    if (rolesData?.body) setRoles(rolesData.body);
  }, [rolesData]);

  useEffect(() => {
    if (addData) {
      toast.success("Admin user created successfully");
      setShowModal(false);
      fetchData();
    }
    if (addError) toast.error("Failed to create admin user");
  }, [addData, addError]);

  useEffect(() => {
    if (updateData) {
      toast.success("Admin user updated successfully");
      setShowModal(false);
      fetchData();
    }
    if (updateError) toast.error("Failed to update admin user");
  }, [updateData, updateError]);

  useEffect(() => {
    if (deleteData) {
      toast.success("Admin user deactivated successfully");
      fetchData();
    }
    if (deleteError) toast.error("Failed to deactivate admin user");
  }, [deleteData, deleteError]);

  const openModal = (type, data = null) => {
    setAction(type);
    if (type === "add") {
      setForm({ name: "", email: "", phone: "", password: "", role_id: 1 });
      setSelectedId(null);
    } else {
      setForm({
        name: data.name,
        email: data.email,
        phone: data.phone,
        password: "",
        role_id: data.role_id || 1,
      });
      setSelectedId(data.admin_id);
    }
    setShowModal(true);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }

    if (action === "add") {
      if (!form.password) {
        toast.error("Password is required");
        return;
      }
      addAdminUser({ token, data: form });
      return;
    }

    const payload = {
      name: form.name,
      phone: form.phone,
      role_id: Number(form.role_id),
    };
    if (form.password) payload.password = form.password;

    updateAdminUser({ token, id: selectedId, data: payload });
  };

  return (
    <div className="page-content container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Admin Users</h4>
        <button className="btn btn-primary btn-sm" onClick={() => openModal("add")}>
          <HiOutlinePlus className="mr-1" /> Register Admin
        </button>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.admin_id}>
                  <td>{index + 1}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.phone}</td>
                  <td>{user.role_name || "Super Admin"}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-secondary mr-2"
                      onClick={() => openModal("edit", user)}
                    >
                      <AiOutlineEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deactivateAdminUser({ token, id: user.admin_id })}
                    >
                      <AiOutlineDelete />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {action === "add" ? "Register Admin User" : "Edit Admin User"}
                  </h5>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      className="form-control"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      disabled={action === "edit"}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      className="form-control"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      className="form-control"
                      value={form.role_id}
                      onChange={(e) => handleChange("role_id", e.target.value)}
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{action === "add" ? "Password" : "New Password (optional)"}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
